import React from 'react';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Toggle from 'rsuite/Toggle';
import { InputWrapper } from '../../../common/input-wrapper';
import {
  accountValidation,
  bicValidation,
} from '../../../../utils/validations';
import { Company, InnSelect } from '../../../common/inn-select';
import { FormAccountBank } from './add-customs-invoice-account-modal';

type BankItemProps = {
  index: number;
  control: any;
  fields: any[];
  remove: (index: number) => void;
  setValue: (name: any, value: any) => void;
  bank: FormAccountBank;
};

export const BankItem: React.FC<BankItemProps> = ({
  index,
  control,
  fields,
  remove,
  setValue,
  bank,
}) => {
  const intl = useIntl();

  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="mt-3 border-bottom pb-2">
      <div className="d-flex gap-2 align-items-center ">
        <button
          type="button"
          className="d-flex gap-2 align-items-center list-group-item  "
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <i className="bi bi-chevron-up"></i>
          ) : (
            <i className="bi bi-chevron-down"></i>
          )}
        </button>
        <span className="fw-500">
          {bank.bankName ? bank.bankName : `Счет №${index + 1}`}
        </span>
        <div className="d-flex mb-3 ms-auto">
          <Controller
            name={`banks.${index}.disabled`}
            control={control}
            render={({ field, fieldState }) => {
              return (
                <InputWrapper error={fieldState.error?.message}>
                  <Toggle
                    checked={!field.value}
                    onChange={(val) => {
                      field.onChange(!val);
                      if (val) {
                        fields.forEach((_, i) => {
                          if (i !== index) {
                            setValue(`banks.${i}.disabled`, true);
                          }
                        });
                      }
                    }}
                  />
                </InputWrapper>
              );
            }}
          />
        </div>
        {fields.length > 1 && (
          <button
            type="button"
            className="btn btn-outline-danger ms-2"
            onClick={() => remove(index)}
          >
            <i className="bi bi-trash p-1 cursor-pointer" />
          </button>
        )}
      </div>
      {expanded && (
        <>
          <div className="row">
            <Controller
              name={`banks.${index}.account`}
              control={control}
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Укажите счет',
                  id: 'form.label.AddInvoiceAccountAccount',
                  description: 'Пояснение к полю ввода',
                }),
                ...accountValidation,
              }}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper
                    title="Счет"
                    className="col"
                    error={fieldState.error?.message}
                    required
                  >
                    <div className="input-group">
                      <input
                        className="form-control"
                        type="text"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </div>
                  </InputWrapper>
                );
              }}
            />

            <Controller
              name={`banks.${index}.bankInn`}
              control={control}
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Укажите ИНН',
                  id: 'form.label.AddInvoiceAccountSigningInn',
                  description: 'Пояснение к полю ввода',
                }),
              }}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper
                    required
                    title={intl.formatMessage({
                      defaultMessage: 'ИНН Банка',
                      id: 'form.input-label.AddInvoiceAccountBankInn',
                      description: 'Заголовок поля ввода',
                    })}
                    error={fieldState.error?.message}
                  >
                    {!field.value ? (
                      <InnSelect
                        {...field}
                        value={
                          field.value
                            ? ({
                                inn: field.value,
                                name: field.value,
                              } as Company)
                            : null
                        }
                        placeholder={intl.formatMessage({
                          defaultMessage: 'Введите название компании или ИНН',
                          id: 'form.input-label.enterInvoiceAccountBankInn',
                          description: 'Поле ввода',
                        })}
                        onSelectCompany={(selectedCompany) => {
                          setValue(
                            `banks.${index}.bankInn`,
                            selectedCompany.inn,
                          );
                          setValue(
                            `banks.${index}.bankName`,
                            selectedCompany.name,
                          );
                          setValue(
                            `banks.${index}.bankAddress`,
                            selectedCompany.address,
                          );
                        }}
                      />
                    ) : (
                      <div className="input-group">
                        <input
                          className="form-control"
                          type="text"
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.currentTarget.value);
                          }}
                        />
                      </div>
                    )}
                  </InputWrapper>
                );
              }}
            />
          </div>
          <div className="row mt-3">
            <Controller
              name={`banks.${index}.bankName`}
              control={control}
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Укажите наименование банка',
                  id: 'form.label.enterInvoiceAccountBankName',
                  description: 'Пояснение к полю ввода',
                }),
              }}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper
                    title="Наименование"
                    className="col"
                    error={fieldState.error?.message}
                    required
                  >
                    <div className="input-group">
                      <input
                        className="form-control"
                        type="text"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </div>
                  </InputWrapper>
                );
              }}
            />
          </div>
          <div className="row mt-3">
            <Controller
              name={`banks.${index}.bankAddress`}
              control={control}
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Укажите адрес',
                  id: 'form.label.enterInvoiceAccountBankAddress',
                  description: 'Пояснение к полю ввода',
                }),
              }}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper
                    title="Адрес банка"
                    className="col"
                    error={fieldState.error?.message}
                    required
                  >
                    <div className="input-group">
                      <input
                        className="form-control"
                        type="text"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </div>
                  </InputWrapper>
                );
              }}
            />
          </div>
          <div className="row mt-3">
            <Controller
              name={`banks.${index}.bankAccount`}
              control={control}
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Укажите счет',
                  id: 'form.label.enterInvoiceAccountBankAccount',
                  description: 'Пояснение к полю ввода',
                }),
                ...accountValidation,
              }}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper
                    title="Корреспондентский счет"
                    className="col"
                    error={fieldState.error?.message}
                    required
                  >
                    <div className="input-group">
                      <input
                        className="form-control"
                        type="text"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </div>
                  </InputWrapper>
                );
              }}
            />
            <Controller
              name={`banks.${index}.bankBic`}
              control={control}
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Укажите БИК',
                  id: 'form.label.enterInvoiceAccountBankBic',
                  description: 'Пояснение к полю ввода',
                }),
                ...bicValidation,
              }}
              render={({ field, fieldState }) => {
                return (
                  <InputWrapper
                    title="БИК"
                    className="col"
                    error={fieldState.error?.message}
                    required
                  >
                    <div className="input-group">
                      <input
                        className="form-control"
                        type="text"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </div>
                  </InputWrapper>
                );
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};
